import { fetchTARealWithCorsHash } from 'api/resort/static';
import { TAResponse3 } from 'api/tripadvisor/types';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Diagnostics } from 'util/diagnostics';
import { getPortalConfig } from 'util/getPortalConfig';
import { isAxiosError } from 'util/isAxiosError';

export interface IOfferState {
  forKey: string;
  offerMap: Record<string, Array<TAResponse3.Offer>>;
  isComplete: boolean;
  isPolling: boolean;
  pollingLink: string;
  hasData: boolean;
  iteration: number;
  error?: string;
}

export const useOffers = (locations: Array<string>, checkIn: string, checkOut: string, numAdults: number, numRooms: number) => {
  const memoKey = useMemo(
    () => [
      ...locations.sort(),
      checkIn,
      checkOut,
      numAdults,
      numRooms,
    ].join(':'), [locations, checkIn, checkOut, numRooms, numRooms],
  );

  const keyRef = useRef<string>(memoKey);

  const [state, setState] = useState<IOfferState>({
    forKey: '',
    offerMap: {},
    isComplete: false,
    isPolling: false,
    pollingLink: '',
    hasData: false,
    iteration: 0,
  });

  useEffect(() => {
    // Assign new ref and trigger initial fetch
    keyRef.current = memoKey;
    setState({
      forKey: memoKey,
      offerMap: {},
      isComplete: false,
      isPolling: false,
      pollingLink: '',
      hasData: false,
      iteration: 0,
    });
  }, [memoKey]);

  const config = getPortalConfig();

  useEffect(() => {
    if (memoKey === state.forKey && !state.hasData) {
      const fetch = async () => {
        Diagnostics.debug('Initial fetching', memoKey, locations);
        try {
          if (locations.length) {
            // No data yet, start request
            const data = await fetchTARealWithCorsHash(
              locations,
              checkIn,
              checkOut,
              numAdults,
              numRooms,
              config.options.currency,
            );
            Diagnostics.debug('Initial fetching done', memoKey);
            // Validate if data is for needed key
            if (keyRef.current !== memoKey) {
              Diagnostics.debug('Data is obsolete, omitting');
              return;
            }
            const result: { [location: string]: Array<TAResponse3.Offer> } = {};
            let error: string | undefined;
            if (data && data.success && data.success.results) {
              data.success.results.forEach((r) => {
                result[r.hotelId] = (r.offers ?? []).filter((o) => !!o);
              });
            } else {
              error = 'Something Went Wront, No Results Found';
            }
            if (data && data.errors && data.errors.length) {
              error = data.errors[0].message;
            }
            setState({
              error,
              forKey: memoKey,
              hasData: true,
              pollingLink: data.success?.pollingLink,
              isComplete: data.success ? data.success.isComplete : true,
              isPolling: false,
              offerMap: result,
              iteration: 0,
            });
          } else {
            setState({
              forKey: memoKey,
              hasData: false,
              pollingLink: '',
              isComplete: true,
              isPolling: false,
              offerMap: {},
              iteration: 0,
            });
          }
        } catch (e) {
          console.error(e);
          if (isAxiosError(e)) {
            const data: TAResponse3.Root = e.response?.data;
            if (data && data.errors && data.errors.length) {
              setState({
                error: data.errors[0].messageKey || data.errors[0].message,
                forKey: memoKey,
                hasData: true,
                pollingLink: '',
                isComplete: true,
                isPolling: false,
                offerMap: {},
                iteration: 0,
              });
              return;
            }
          }
          setState({
            error: 'TripAdvisor could not fetch any results, please check dates you entered',
            forKey: memoKey,
            hasData: true,
            pollingLink: '',
            isComplete: true,
            isPolling: false,
            offerMap: {},
            iteration: 0,
          });
        }
      };
      if (checkIn && checkOut) {
        fetch().catch((error) => Diagnostics.error({ error }));
      }
    }
  }, [memoKey, state.hasData, state.forKey]);

  useEffect(() => {
    if (memoKey === state.forKey && !state.isComplete && !state.isPolling && state.hasData) {
      const fetch = async () => {
        Diagnostics.debug('Poll started for key', memoKey);
        if (keyRef.current !== memoKey) {
          Diagnostics.debug('Data is obsolete, omitting');
          return;
        }
        Diagnostics.debug('Poll fetching', memoKey);
        // No data yet, start request
        const data = await fetchTARealWithCorsHash(
          locations,
          checkIn,
          checkOut,
          numAdults,
          numRooms,
          config.options.currency,
        );

        Diagnostics.debug('Poll complete', memoKey);
        // Validate if data is for needed key
        if (keyRef.current !== memoKey) {
          Diagnostics.debug('Data is obsolete, omitting');
          return;
        }
        const result: { [location: string]: Array<TAResponse3.Offer> } = {};
        if (data) {
          data.success.results.forEach((r) => {
            result[r.hotelId] = (r.offers ?? []).filter((o) => !!o);
          });
        }
        setState({
          forKey: memoKey,
          hasData: true,
          pollingLink: data.success.pollingLink,
          isComplete: data.success.isComplete,
          isPolling: false,
          offerMap: result,
          iteration: state.iteration + 1,
        });
      };
      Diagnostics.debug('Scheduling poll', memoKey, state.iteration);
      setTimeout(() => {
        fetch().catch((error) => Diagnostics.error({ error }));
      }, 1000);
    }
  }, [memoKey, state.iteration, state.isPolling, state.forKey, state.isComplete, state.pollingLink, state.hasData]);

  return {
    error: state.error,
    offerMap: state.offerMap,
    areOffersLoading: !state.hasData,
  };
};
